// @ts-strict-ignore
import { ArticleTypeIds } from '@article/constants/article-type-ids';
import { AttachedArticleContainer } from '@project/components/article/service/article-container-attached.service';
import { ProjectModel } from '@project/model/project.model';
import { ArticleAccessoryManager } from '@article/manager/article-accessory.manager';
import { MountingRailHelper } from '@project/helper/mounting-rail.helper';
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { Line } from '@3d-scene/model/line';
import { ArticleUtils } from '@article/util/article-utils';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { ArticleUsageVO } from '@article/model/vo/article.vo';
import { CarrierBlockVO, ProjectVO } from '@project/model/vo/project.vo';
import { Vector3 } from 'three';
import { Dictionary } from 'typescript-collections';
import { UnitConverter } from '@3d-scene/common/util/unit-converter';
import { CarrierBlockUtils } from '@project/components/article/util/carrier-block-utils';
import { MoveArticlesBetweenBlocksData } from '@shared/undo/data/move-articles-between-blocks-data';
import { Injectable, OnDestroy } from '@angular/core';
import { Log } from '@shared/debug/log';
import { Subscription } from 'rxjs';
import { InsertedArticleData } from '../processor/i-article-location.processor';
import { InsertArticleService } from '../service/insert-article.service';
import { ArticleConnectionService } from '@3d-scene/model3d/article-connection.service';
import { MovedArticlesInBlock } from '../presenter/move-article.presenter';
import { AdvancedLine } from '@3d-scene/model/advanced-line';

type BlockGuidAndBmk = { blockGuid: string; bmkName: string }

@Injectable({ providedIn: 'root' })
export class CarrierBlockManager implements OnDestroy {
  public static readonly ARTICLE_IDS_EXCLUDED_FROM_BLOCKS: number[] = [
    ArticleTypeIds.BLOCKING_ZONE,
    ArticleTypeIds.EMPTY_ZONE,
    ArticleTypeIds.DRILLING_HOLE,
  ];

  public isActive: boolean = true;

  private _emptyCarrierBlocks = [];
  private _loadedArticlesSubscription: Subscription;

  private readonly _carrierBlockMessageTypes = [
    'addMcsGdsArticle',
    'insertDrillingHoleArticle',
    'insertUndoableDrillingHoleArticle',
    'insertPresetDrillingHoleArticle',
    'insertUndoablePresetDrillingHoleArticle',
    'addArticleProcess',
    'insertArticleByLocation',
    'insertAccessoryOnRail',
    'insertAccessoryByNeighbour'
  ]

  constructor(
    private projectModel: ProjectModel,
    private articleAccessoryManager: ArticleAccessoryManager,
    private mountingRailHelper: MountingRailHelper,
    private containerService: ContainerService,
    private articleConnectionService: ArticleConnectionService,
    private _insertArticleService: InsertArticleService
  ) {
    this.subscribeOnArticlesLoaded()
  }

  ngOnDestroy(): void {
    this._loadedArticlesSubscription?.unsubscribe()
    this._loadedArticlesSubscription = undefined;
  }

  private subscribeOnArticlesLoaded():void {
    this._loadedArticlesSubscription = this._insertArticleService.articleInserted$.subscribe({
      next: (data) => {
        if (!data.error) {
          this.onArticleLoaded(data)
        }
      },
      error: (error) => Log.error('Error in articlesLoadedNotification subscription: ', error)
    })
  }

  private onArticleLoaded(data: { messageType: string, insertedArticleData?: InsertedArticleData }): void {
    if (this._carrierBlockMessageTypes.includes(data.messageType)) {
      if (!this.isActive || data.insertedArticleData.isAccessoriesAdded) {
        return;
      }
      const insertedArticleGuids: string[] = data.insertedArticleData.insertedArticleGUIDs;
      this.includeArticlesInNeighborBlock(insertedArticleGuids);
    }
  }

  public includeArticlesInNeighborBlock(articleGuids: string[]): BlockGuidAndBmk {
    if (ArrayUtils.isEmpty(this.projectModel.currentProject.carrierBlocks) || ArrayUtils.isEmpty(articleGuids)) {
      return null;
    }

    const articleContainers: ArticleContainer[] = [];
    for (const insertedGuid of articleGuids) {
      const insertedContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(insertedGuid);
      if (!insertedContainer) {
        continue;
      }
      articleContainers.push(insertedContainer);
    }

    return this.includeArticleContainersInNeighborBlock(articleContainers);
  }

  public includeArticleContainersInNeighborBlock(articleContainers: ArticleContainer[]): BlockGuidAndBmk {
    if (ArrayUtils.isEmpty(this.projectModel.currentProject.carrierBlocks) || ArrayUtils.isEmpty(articleContainers)) {
      return null;
    }

    const articleContainersOnRail: ArticleContainer[] = [];
    for (const articleContainer of articleContainers) {
      if (
        /*CaeProjectImportUtils.isArticleExcluded(articleContainer.article) ||*/
        CarrierBlockUtils.getCarrierBlockGuid(articleContainer.articleUsage)
      ) {
        continue;
      }
      if (this.articleConnectionService.isArticleConnectedToMountingRail(articleContainer)) {
        articleContainersOnRail.push(articleContainer);
      }
    }
    let newCarrierBlockGuid: BlockGuidAndBmk = null;
    if (articleContainersOnRail.length > 0) {
      newCarrierBlockGuid = this.processInsertedArticlesOnRail(articleContainersOnRail);
    }
    return newCarrierBlockGuid;
  }

  private processInsertedArticlesOnRail(articleContainersOnRail: ArticleContainer[]): BlockGuidAndBmk {
    const insertHelper: InsertArticleHelper = this.mountingRailHelper.getInsertArticleHelper();
    if (!insertHelper) {
      return null;
    }

    if (articleContainersOnRail.length > 1) {
      articleContainersOnRail = insertHelper.sortArticles(articleContainersOnRail);
    }

    const firstContainer: ArticleContainer = articleContainersOnRail[0];
    const firstLine: Line = insertHelper.getArticleLine(firstContainer);

    const lastContainer: ArticleContainer = articleContainersOnRail[articleContainersOnRail.length - 1];
    const lastLine: Line = insertHelper.getArticleLine(lastContainer);

    const leftNeighborContainer: ArticleContainer = insertHelper.getLeftArticleContainer(
      firstContainer.articleGUID,
      CarrierBlockManager.ARTICLE_IDS_EXCLUDED_FROM_BLOCKS
    );
    const leftNeighborLine: Line = leftNeighborContainer == null ? null : insertHelper.getArticleLine(leftNeighborContainer);

    const rightNeighborContainer: ArticleContainer = insertHelper.getRightArticleContainer(
      lastContainer.articleGUID,
      CarrierBlockManager.ARTICLE_IDS_EXCLUDED_FROM_BLOCKS
    );
    const rightNeighborLine: Line = rightNeighborContainer == null ? null : insertHelper.getArticleLine(rightNeighborContainer);

    const distanceToLeftBlock: number = !leftNeighborLine ? NaN : leftNeighborLine.end.distanceTo(firstLine.start);
    const distanceToRightBlock: number = !rightNeighborLine ? NaN : lastLine.end.distanceTo(rightNeighborLine.start);

    if (isNaN(distanceToLeftBlock) && isNaN(distanceToRightBlock)) {
      return null; // no neighbor articles found, so block can not be determined
    }

    let targetNeighborContainer: ArticleContainer = null;
    const compareResult: number = this.compareDistances(distanceToLeftBlock, distanceToRightBlock);
    if (compareResult > 0) {
      // right neighbor is closer or there is no left neighbor
      targetNeighborContainer = rightNeighborContainer;
    } else if (compareResult < 0) {
      // left neighbor is closer or there is no right neighbor
      targetNeighborContainer = leftNeighborContainer;
    } else if (articleContainersOnRail.length === 1) {
      targetNeighborContainer = this.determineEquidistantNeighbor(firstContainer, leftNeighborContainer, rightNeighborContainer);
    } else {
      targetNeighborContainer = leftNeighborContainer;
    }

    const blockGuid: string = CarrierBlockUtils.getCarrierBlockGuid(targetNeighborContainer.articleUsage);
    const bmkName: string = CarrierBlockUtils.getBmkName(targetNeighborContainer.articleUsage);
    if (blockGuid) {
      for (const containerOnRail of articleContainersOnRail) {
        CarrierBlockUtils.setCarrierBlockGuidAndBmk(containerOnRail.articleUsage, blockGuid, bmkName);
      }
      this.addArticleGuidsToCarrierBlock(ArticleContainerUtils.extractArticleGUIDs(articleContainersOnRail), blockGuid);
    }
    return {blockGuid, bmkName };
  }

  private determineEquidistantNeighbor(
    containerToCheck: ArticleContainer,
    leftNeighbor: ArticleContainer,
    rightNeighbor: ArticleContainer
  ): ArticleContainer {
    let targetNeighbor: ArticleContainer = null;
    if (ArticleUtils.isEndPlateArticle(containerToCheck.article)) {
      targetNeighbor = this.determineNeighborByEndPlate(containerToCheck, leftNeighbor, rightNeighbor);
    } else if (ArticleUtils.isEndStopArticle(containerToCheck.article)) {
      targetNeighbor = this.determineNeighborByEndStop(containerToCheck, leftNeighbor, rightNeighbor);
    } else {
      targetNeighbor = leftNeighbor;
    }
    return targetNeighbor;
  }
  private determineNeighborByEndPlate(
    endPlateContainer: ArticleContainer,
    leftNeighbor: ArticleContainer,
    rightNeighbor: ArticleContainer
  ): ArticleContainer {
    const endPlateArticleId: number = endPlateContainer.article.identity;
    const isLeftAccessory: boolean = this.articleAccessoryManager.isAccessoryArticleExists(leftNeighbor.article.identity, endPlateArticleId);
    if (isLeftAccessory && ArticleContainerUtils.isRightSideOpen(leftNeighbor)) {
      return leftNeighbor;
    }
    const isRightAccessory: boolean = this.articleAccessoryManager.isAccessoryArticleExists(rightNeighbor.article.identity, endPlateArticleId);
    if (isRightAccessory && ArticleContainerUtils.isLeftSideOpen(rightNeighbor)) {
      return rightNeighbor;
    }
    return leftNeighbor;
  }

  private determineNeighborByEndStop(
    endStopContainer: ArticleContainer,
    leftNeighbor: ArticleContainer,
    rightNeighbor: ArticleContainer
  ): ArticleContainer {
    if (!ArticleUtils.isEndStopArticle(leftNeighbor.article)) {
      return leftNeighbor;
    }
    if (!ArticleUtils.isEndStopArticle(rightNeighbor.article)) {
      return rightNeighbor;
    }
    return leftNeighbor;
  }

  private compareDistances(distanceA: number, distanceB: number): number {
    if (isNaN(distanceA) && isNaN(distanceB)) {
      return 0;
    }
    if (isNaN(distanceA)) {
      return 1;
    }
    if (isNaN(distanceB)) {
      return -1;
    }
    return MathUtils.compare(distanceA, distanceB);
  }

  public processAttachedArticleContainer(data: AttachedArticleContainer): void {
    if (!this.isActive || !data.articleGUID) {
      return;
    }
    const articleUsage: ArticleUsageVO = this.projectModel.getArticleUsage(data.articleGUID);
    if (!articleUsage) {
      return;
    }

    const carrierBlockGuid: string = CarrierBlockUtils.getCarrierBlockGuid(articleUsage);
    if (carrierBlockGuid) {
      this.addArticleGuidsToCarrierBlock([data.articleGUID], carrierBlockGuid);
    }
  }

  public articleContainerDetachedHandler(articleContainer: ArticleContainer): void {
    if (!this.isActive || !articleContainer) {
      return;
    }
    const carrierBlockGuid: string = CarrierBlockUtils.getCarrierBlockGuid(articleContainer.articleUsage);
    this.removeArticleGuidsFromCarrierBlock([articleContainer.articleGUID], carrierBlockGuid);
  }

  public articleContainersMovedFromCarrierBlock(data: any): void {
    if (!this.isActive) {
      return;
    }
    this.addCarrierBlockPropertyForArticles(data.moveArticlesBetweenBlocksDatas, data.restoreBlock);
  }

  public addArticleGuidsToCarrierBlock(articleGuids: string[], carrierBlockGuid: string, carrierBlockName = carrierBlockGuid): void {
    if (ArrayUtils.isEmpty(articleGuids) || !this.projectModel.currentProject || !carrierBlockGuid) {
      return;
    }
    const carrierBlock = this.getOrCreateCarrierBlock(carrierBlockGuid, carrierBlockName);

    if (ArrayUtils.isEmpty(carrierBlock.articleGuids)) {
      carrierBlock.articleGuids = [];
    }
    for (const articleGuid of articleGuids) {
      if (!carrierBlock.containsArticleGuid(articleGuid)) {
        carrierBlock.articleGuids.push(articleGuid);
      }
      carrierBlock.guidsSorted = false;
    }
  }

  public removeArticleContainersFromBlock(articleContainers: ArticleContainer[]): void {
    for (const articleContainer of articleContainers) {
      const blockGuid: string = CarrierBlockUtils.getCarrierBlockGuid(articleContainer.articleUsage);
      if (blockGuid) {
        this.removeArticleGuidsFromCarrierBlock([articleContainer.articleGUID], blockGuid);
        CarrierBlockUtils.removeCarrierBlockGuidAndBmk(articleContainer.articleUsage);
      }
    }
  }

  private removeArticleGuidsFromCarrierBlock(articleGuids: string[], carrierBlockGuid: string): void {
    if (ArrayUtils.isEmpty(articleGuids) || !this.projectModel.currentProject || !carrierBlockGuid) {
      return;
    }
    const carrierBlock: CarrierBlockVO = CarrierBlockUtils.getCarrierBlockInProjectByGuid(this.projectModel.currentProject, carrierBlockGuid);
    if (!carrierBlock) {
      return;
    }

    if (ArrayUtils.isNotEmpty(carrierBlock.articleGuids)) {
      for (const articleGuid of articleGuids) {
        const removeIndex: number = carrierBlock.articleGuids.indexOf(articleGuid);
        if (removeIndex > -1) {
          carrierBlock.articleGuids.splice(removeIndex, 1);
        }
      }
    }
  }

  public addCarrierBlockPropertyForArticles(blockGuidToArticleGuids: MoveArticlesBetweenBlocksData[], restoreBlock: boolean): void {
    for (const moveArticlesBetweenBlocksData of blockGuidToArticleGuids) {
      const blockGuidToAddArticle: string = restoreBlock ? moveArticlesBetweenBlocksData.fromBlockGuid : moveArticlesBetweenBlocksData.toBlockGuid;
      const blockGuidToRemoveArticle: string = restoreBlock ? moveArticlesBetweenBlocksData.toBlockGuid : moveArticlesBetweenBlocksData.fromBlockGuid;
      const bmkNameToAddArticle: string = restoreBlock ? moveArticlesBetweenBlocksData.fromBmkStructure : moveArticlesBetweenBlocksData.toBmkStructure;

      this.removeArticleGuidsFromCarrierBlock([moveArticlesBetweenBlocksData.articleGuid], blockGuidToRemoveArticle);
      const usage: ArticleUsageVO = this.projectModel.getArticleUsage(moveArticlesBetweenBlocksData.articleGuid);
      if (!blockGuidToAddArticle) {
        CarrierBlockUtils.removeCarrierBlockGuidAndBmk(usage);
      } else {
        this.addArticleGuidsToCarrierBlock([moveArticlesBetweenBlocksData.articleGuid], blockGuidToAddArticle);
        CarrierBlockUtils.setCarrierBlockGuidAndBmk(usage, blockGuidToAddArticle, bmkNameToAddArticle);
      }
    }
  }

  public updateCarrierBlocksInProject(project: ProjectVO): void {
    if (!project || ArrayUtils.isEmpty(project.carrierBlocks)) {
      return;
    }

    const carrierBlocks: CarrierBlockVO[] = project.carrierBlocks;
    this.removeEmptyCarrierBlocks(carrierBlocks);

    const blockIdToBlockLine: Dictionary<string, Line> = new Dictionary<string, Line>();
    const insertArticleHelper: InsertArticleHelper = this.mountingRailHelper.getInsertArticleHelper();
    if (!insertArticleHelper) {
      return;
    }

    // sorting articles inside blocks
    for (const carrierBlock of carrierBlocks) {
      // carrier-group-guid isn't stored in end-stops, so special consideration of end-stops is not needed here
      const articleContainers: ArticleContainer[] = this.sortArticlesInCarrierBlock(carrierBlock, insertArticleHelper, true);
      if (articleContainers.length > 0) {
        const firstArticleLine: Line = insertArticleHelper.getArticleLine(articleContainers[0]);
        const lastArticleLine: Line = insertArticleHelper.getArticleLine(articleContainers[articleContainers.length - 1]);
        blockIdToBlockLine.setValue(carrierBlock.guid, new Line(firstArticleLine.start, lastArticleLine.end));
      }
    }

    const updatedCarrierBlocks: CarrierBlockVO[] = this.updateSpaceForCarrierBlocks(
      carrierBlocks,
      blockIdToBlockLine,
      insertArticleHelper.startPoint
    );
    project.carrierBlocks = updatedCarrierBlocks;
  }

  public sortArticlesInCarrierBlock(
    carrierBlock: CarrierBlockVO,
    insertArticleHelper: InsertArticleHelper,
    retrieveSortedContainers: boolean = false
  ): ArticleContainer[] {
    if (!insertArticleHelper || !carrierBlock) {
      return [];
    }
    if (carrierBlock.guidsSorted && !retrieveSortedContainers) {
      return [];
    }

    let articleContainers: ArticleContainer[] = this.containerService.findArticleContainersInGlobalByGuids(carrierBlock.articleGuids);
    if (!carrierBlock.guidsSorted && articleContainers.length > 0) {
      articleContainers = insertArticleHelper.sortArticles(articleContainers);
      carrierBlock.articleGuids = ArticleContainerUtils.extractArticleGUIDs(articleContainers);
      carrierBlock.guidsSorted = true;
    }
    return articleContainers;
  }

  private removeEmptyCarrierBlocks(carrierBlocks: CarrierBlockVO[]): void {
    this._emptyCarrierBlocks = [];
    for (const carrierBlock of carrierBlocks.concat()) {
      if (carrierBlock.isEmpty()) {
        this._emptyCarrierBlocks = this._emptyCarrierBlocks.concat(carrierBlocks.splice(carrierBlocks.indexOf(carrierBlock), 1));
      }
    }
  }

  public restoreEmptyProjectCarrierBlocks(): void {
    if (ArrayUtils.isEmpty(this._emptyCarrierBlocks)) {
      return;
    }
    ArrayUtils.mergeArrays(this.projectModel.currentProject.carrierBlocks, this._emptyCarrierBlocks);
    this._emptyCarrierBlocks = [];
  }

  private updateSpaceForCarrierBlocks(
    carrierBlocks: CarrierBlockVO[],
    blockIdToBlockLine: Dictionary<string, Line>,
    baseLineStart: Vector3
  ): CarrierBlockVO[] {
    const sortedCarrierBlocks: CarrierBlockVO[] = this.sortCarrierBlocksByPosition(carrierBlocks, blockIdToBlockLine);
    let previousBlockEndPoint: Vector3 = baseLineStart;
    // update/re-calculate carrier block space value
    // (distance to previous block or to the beginning of mounting rail in case of the first block)
    for (const sortedCarrierBlock of sortedCarrierBlocks) {
      const blockLine: Line = blockIdToBlockLine.getValue(sortedCarrierBlock.guid);
      const blockDistance: number = previousBlockEndPoint.distanceTo(blockLine.start);
      sortedCarrierBlock.space = UnitConverter.fromToRounded('3d', 'mm', blockDistance);
      previousBlockEndPoint = blockLine.end;
    }
    return sortedCarrierBlocks;
  }

  private sortCarrierBlocksByPosition(carrierBlocks: CarrierBlockVO[], blockIdToBlockLine: Dictionary<string, Line>): CarrierBlockVO[] {
    return [...carrierBlocks].sort((carrierBlock) => -blockIdToBlockLine.getValue(carrierBlock.guid).start.x);
  }

  public getMoveArticlesBetweenBlocksDatas(movedArticleContainers: ArticleContainer[], insertHelper: InsertArticleHelper,
                                            commonLine: Line): MoveArticlesBetweenBlocksData[] {
    if (ArrayUtils.isEmpty(movedArticleContainers) || !insertHelper || !commonLine) {
      return null;
    }
    const carrierBlocksInProject: CarrierBlockVO[] = this.projectModel.currentProject.carrierBlocks;
    if (ArrayUtils.isEmpty(carrierBlocksInProject)) {
      return null;
    }
    const groupedArticlesByBlocks: MovedArticlesInBlock[] = this.groupArticlesByCarrierBlocks(movedArticleContainers);

    if (ArrayUtils.isEmpty(groupedArticlesByBlocks)) {
      return null;
    }

    const leftBlockGuid: string = this.getCarrierBlockGuidAtCommonLine(insertHelper, commonLine, true);
    const rightBlockGuid: string = this.getCarrierBlockGuidAtCommonLine(insertHelper, commonLine, false);

    if (!leftBlockGuid && !rightBlockGuid) {
      return null;
    }

    let updateCarierBlockForMovedArticles: boolean = false;
    const blocksWithChangedSorting: CarrierBlockVO[] = [];
    for (const movedArticlesInBlock of groupedArticlesByBlocks) {
      const movedBlockInProject: CarrierBlockVO = CarrierBlockUtils.getCarrierBlockByGuid(carrierBlocksInProject,
        movedArticlesInBlock.blockGuid);
      if (movedBlockInProject.articleGuids.length !== movedArticlesInBlock.articleGuids.length) {
        const movedBlockId: string = movedArticlesInBlock.blockGuid;
        const moveBlockIndex: number = groupedArticlesByBlocks.indexOf(movedArticlesInBlock);
        if (moveBlockIndex === 0 && (leftBlockGuid !== movedBlockId && (groupedArticlesByBlocks.length > 1 ||
            rightBlockGuid !== movedBlockId)) // check if moved block is placed at start of moved blocks
          || moveBlockIndex === groupedArticlesByBlocks.length - 1 && groupedArticlesByBlocks.length > 1 &&
          rightBlockGuid !== movedBlockId // check if moved block is placed at the end of moved blocks
          || moveBlockIndex > 0 && moveBlockIndex < groupedArticlesByBlocks.length - 1) {
          // check if moved block is placed in the middle of moved blocks
          updateCarierBlockForMovedArticles = true;
          break;
        } else {
          // articles are moved inside of a block, so sorting could be changed
          blocksWithChangedSorting.push(movedBlockInProject);
          movedBlockInProject.guidsSorted = false;
        }
      } else if (leftBlockGuid === rightBlockGuid) {// check if the whole block is inserted into another block
        updateCarierBlockForMovedArticles = true;
        break;
      }
    }

    const moveArticlesBetweenBlocksDatas: MoveArticlesBetweenBlocksData[] = [];
    if (updateCarierBlockForMovedArticles) {
      this.removeArticleContainersFromBlock(movedArticleContainers);
      const newCarrierBlock: BlockGuidAndBmk = this.includeArticleContainersInNeighborBlock(movedArticleContainers);
      for (const movedArticlesInBlock of groupedArticlesByBlocks) {
        for (const articleGuid of movedArticlesInBlock.articleGuids) {
          moveArticlesBetweenBlocksDatas.push(new MoveArticlesBetweenBlocksData(articleGuid, movedArticlesInBlock.blockGuid, movedArticlesInBlock.bmkName,
            newCarrierBlock.blockGuid, newCarrierBlock.bmkName));
        }
      }
      if (!newCarrierBlock) {
        // remove block guid property from articles
        this.addCarrierBlockPropertyForArticles(moveArticlesBetweenBlocksDatas, false);
      }
    } else {
      for (const blockWithChangedSorting of blocksWithChangedSorting) {
        blockWithChangedSorting.guidsSorted = false;
      }
    }

    return moveArticlesBetweenBlocksDatas;
  }

  private getOrCreateCarrierBlock(blockGuid: string, carrierBlockName: string): CarrierBlockVO {
    this.projectModel.currentProject.carrierBlocks ??= [];
    const carrierBlockInProject: CarrierBlockVO = CarrierBlockUtils.getCarrierBlockByGuid(this.projectModel.currentProject.carrierBlocks, blockGuid);

    if(carrierBlockInProject) {
      return carrierBlockInProject;
    }

    const newBlock = this.createCarrierBlockVO(blockGuid, carrierBlockName);
    this.projectModel.currentProject.carrierBlocks.push(newBlock);
    return newBlock;
  }

  public findCarrierBlockByBmkName(bmkName: string): CarrierBlockVO | undefined {
    this.projectModel.currentProject.carrierBlocks ??= [];
    return CarrierBlockUtils.findCarrierBlockByBmkName(this.projectModel.currentProject.carrierBlocks, bmkName);
  }

  private groupArticlesByCarrierBlocks(movedArticleContainers: ArticleContainer[]): MovedArticlesInBlock[] {
    const movedArticlesInBlocks: MovedArticlesInBlock[] = [];
    for (const movedArticleContainer of movedArticleContainers) {
      const carrierBlockId: string = CarrierBlockUtils.getCarrierBlockGuid(movedArticleContainer.articleUsage);
      const bmkName: string = CarrierBlockUtils.getBmkName(movedArticleContainer.articleUsage);
      if (carrierBlockId) {
        let articleStored: boolean = false;
        for (const movedArticlesInBlock of movedArticlesInBlocks) {
          if (movedArticlesInBlock.blockGuid === carrierBlockId) {
            articleStored = true;
            movedArticlesInBlock.articleGuids.push(movedArticleContainer.articleGUID);
          }
        }
        if (!articleStored) {
          const movedArticlesInBlock: MovedArticlesInBlock = new MovedArticlesInBlock();
          movedArticlesInBlock.blockGuid = carrierBlockId;
          movedArticlesInBlock.bmkName = bmkName;
          movedArticlesInBlock.articleGuids = [movedArticleContainer.articleGUID];
          movedArticlesInBlocks.push(movedArticlesInBlock);
        }
      }
    }
    return movedArticlesInBlocks;
  }

  private getCarrierBlockGuidAtCommonLine(insertHelper: InsertArticleHelper, commonLine: Line, checkLeftSide: boolean): string {
    let neighborContainers: ArticleContainer[] = [];
    if (checkLeftSide) {
      neighborContainers = insertHelper.getConnectedArticlesFromLine(new AdvancedLine(insertHelper.startPoint, commonLine.start),
        null, false, true);
    } else {
      neighborContainers = insertHelper.getConnectedArticlesFromLine(new AdvancedLine(commonLine.end, insertHelper.endPoint));
    }
    for (const articleContainer of neighborContainers) {
      const carrierBlockId: string = CarrierBlockUtils.getCarrierBlockGuid(articleContainer.articleUsage);
      if (carrierBlockId) {
        return carrierBlockId;
      }
    }
    return null;
  }

  private createCarrierBlockVO(blockStructure: string, carrierBlockName: string): CarrierBlockVO {
    const vo = new CarrierBlockVO();
    vo.name = carrierBlockName;
    vo.guid = blockStructure;
    vo.structure = blockStructure;
    vo.articleGuids = [];
    vo.guidsSorted = false;
    return vo;
  }
}
